import type { SurfaceBan } from "@pax/api-contract/moderation/v1alpha1";
import { APPEAL_COPY } from "@pax/moderation-copy";
import { Button } from "@pax/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, orpc, reachable, requestErrorMessage } from "@/lib/api";
import { AppealProgress } from "./AppealProgress";

/**
 * The way to appeal a live game or comment ban, under its message: Appeal while it can be, then
 * where the appeal stands, with a way to withdraw it, until a moderator decides it.
 */
export function BanAppeal({ ban }: { ban: Pick<SurfaceBan, "id" | "appealable" | "appeal"> }) {
  const [filed, setFiled] = useState<string | null>(null);
  const standing = ban.appeal && ban.appeal.status !== "withdrawn" ? ban.appeal.id : null;
  const appealId = filed ?? standing;
  const appeal = useQuery({
    ...orpc.moderation.appeals.get.queryOptions({
      input: { params: { appealId: appealId ?? "" } },
    }),
    enabled: appealId !== null,
  });
  const file = useMutation({
    mutationFn: () => reachable(api.moderation.appeals.create({ body: { banId: ban.id } })),
    onSuccess: ({ appeal: created }) => setFiled(created.id),
  });

  if (appealId) {
    if (!appeal.data) return null;
    if (appeal.data.appeal.status === "overturned") {
      return <p className="mt-1 text-foreground">{APPEAL_COPY.overturnedBan}</p>;
    }
    if (appeal.data.appeal.status !== "withdrawn") {
      return (
        <div className="mt-1">
          <AppealProgress appeal={appeal.data.appeal} onChange={() => appeal.refetch()} />
        </div>
      );
    }
  }
  if (!ban.appealable) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3">
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto px-0 underline"
        disabled={file.isPending}
        onClick={() => file.mutate()}
      >
        {APPEAL_COPY.cta}
      </Button>
      {file.error && <span className="text-destructive/80">{requestErrorMessage(file.error)}</span>}
    </div>
  );
}
